import * as React from 'react';
import { Check, Close, Back, Next, Keypad } from 'core/img';
import * as ThemeManager from './ThemeManager';
import { SvgIcon } from 'core/ui-next';
import { useHelp } from './ActionButton.HelpProvider';
import { useScreenSize } from './ScreenSize';
import { useOverlayExecutor } from 'core/ui-next/modal';
import { useClickWithFeedback } from './useClickWithFeedback';
import { hexColorsByActionButtonCategory } from 'core/ui/ActionButtonCategoryColors';
import { ActionButtonCategory } from 'core/api';

// Este botón NO usa Chakra a propósito: es un <button> con clases CSS (pos-action-button.less).
// Se hizo por rendimiento, igual que con ProductButton y SaleLocationButton. Con Chakra, cada
// botón pagaba en cada render la serialización de emotion de los estilos de la receta más el
// componente Button, y en la pantalla de venta hay decenas de botones montados a la vez.
//
// Si hay que tocar el aspecto, el sitio es pos-action-button.less, no este fichero.

// Los tipos con nombre fijan el color de fondo; las categorías (los valores del enum) sólo añaden
// una franja de color con el color que les asigna ActionButtonCategoryColors.
export type ActionButtonType = 'primary' | 'secondary' | 'success' | ActionButtonCategory;

export type ButtonSurface = 'legacy-footer' | 'footer' | 'actions-grid' | 'sales-screen' | 'edit-ticket-line-modal';
export type FnKey = 'F1' | 'F2' | 'F3' | 'F4' | 'F5' | 'F6' | 'F7' | 'F8' | 'F9' | 'F10' | 'F11' | 'F12';

export type ActionButtonDefinition = {
	intent?: 'ok' | 'cancel' | 'back' | 'more-options'
	title?: string;
	image?: string | SvgIcon;
	type?: ActionButtonType,
	surface?: ButtonSurface,
	action?: () => Promise<any>;
	fnKey?: FnKey;
};

export function isString(x?: string | SvgIcon): x is string {
	return Object.prototype.toString.call(x) === '[object String]';
}

export function NullActionButton({ surface }: { surface?: ButtonSurface }) {
	return <ActionButton surface={surface} visibility={'hidden'} />;
}

export function AcceptActionButton({ surface, action }: { action?: () => Promise<any>, surface?: ButtonSurface }) {
	return <ActionButton type={'success'} image={Check} surface={surface} action={action} />;
}

export function NextActionButton({ surface, action }: { action?: () => Promise<any>, surface?: ButtonSurface }) {
	return <ActionButton type={'primary'} image={Next} surface={surface} action={action} />;
}

export function KeypadActionButton({ surface, action }: { action?: () => Promise<any>, surface?: ButtonSurface }) {
	return <ActionButton type={'secondary'} image={Keypad} surface={surface} action={action} />;
}

export function CancelActionButton({ surface, action }: { action?: () => Promise<any>, surface?: ButtonSurface }) {
	return <ActionButton type='secondary' image={Close} surface={surface} action={action} />;
}

export function BackActionButton({ surface, action }: { action?: () => Promise<any>, surface?: ButtonSurface }) {
	return <ActionButton type='secondary' image={Back} surface={surface} action={action} />;
}

export type ActionButtonProps = ActionButtonDefinition & { visibility?: 'visible' | 'hidden' };

type StyleWithVars = React.CSSProperties & Record<`--${string}`, string>;

const surfaceClassNames: Record<ButtonSurface, string> = {
	'legacy-footer': 'btn-pos-action-legacy-footer',
	'footer': 'btn-pos-action-footer',
	'actions-grid': 'btn-pos-action-actions-grid',
	'sales-screen': 'btn-pos-action-sales-screen',
	'edit-ticket-line-modal': 'btn-pos-action-edit-line'
};

// Las categorías son los valores numéricos del enum; el resto son los tipos con nombre.
function isCategory(type: ActionButtonType): type is ActionButtonCategory {
	return typeof type === 'number';
}

// La combinación de clases sólo depende de props estables (tipo, surface, dispositivo), así que
// se memoiza por clave y se calcula una vez por combinación en toda la vida de la aplicación.
const classNameCache = new Map<string, string>();

function getClassName(device: 'large' | 'phone', type: ActionButtonType, surface: ButtonSurface, visibility?: 'visible' | 'hidden') {

	const cacheKey = `${device}|${type}|${surface}|${visibility ?? ''}`;
	const cached = classNameCache.get(cacheKey);
	if (cached)
		return cached;

	const className = [
		'btn-pos-action',
		`btn-pos-action-${device}`,
		surfaceClassNames[surface],
		isCategory(type) ? 'btn-pos-action-category' : `btn-pos-action-${type}`,
		visibility === 'hidden' ? 'btn-pos-action-hidden' : ''
	].filter(Boolean).join(' ');

	classNameCache.set(cacheKey, className);
	return className;
}

function getCategoryStyle(type: ActionButtonType): StyleWithVars | undefined {
	// El color de la categoría se pasa por variable CSS para que la única fuente de verdad siga
	// siendo ActionButtonCategoryColors.ts y no haya que duplicar los hex en el .less.
	return isCategory(type)
		? { '--btn-pos-action-category-color': hexColorsByActionButtonCategory[type] }
		: undefined;
}

function ActionButtonIcon({ image }: { image?: string | SvgIcon }) {

	if (isString(image))
		return <img src={image} className='btn-pos-action-icon' alt='' />;

	const Icon = image;
	return Icon ? <Icon className='btn-pos-action-icon' /> : null;
}

function useActionClick(action?: () => Promise<any>) {

	const { execute } = useOverlayExecutor();

	const onClick = React.useCallback(() => action && execute({
		overlay: 'on-start',
		waitForPendingRequests: true,
		action: async () => await action()
	}), [execute, action]);

	// Del hook sólo se usa el onClick: el feedback visual (transition + scale al pulsar) está en
	// el CSS, porque las props _active/transition que devuelve son de Chakra.
	return useClickWithFeedback(onClick).onClick;
}

export const PhoneDeviceActionButton = React.memo(function PhoneDeviceActionButton({ title, image, type, surface, action, visibility }: ActionButtonProps) {

	const { helpActive } = useHelp();
	const onClick = useActionClick(action);

	const buttonType = type ?? 'primary';
	const buttonSurface = surface ?? 'footer';
	const showHelp = helpActive && title !== undefined;

	const icon = <ActionButtonIcon image={image} />;
	const text = <span className='btn-pos-action-text'>{title}</span>;

	return (
		<button
			type='button'
			className={getClassName('phone', buttonType, buttonSurface, visibility)}
			style={getCategoryStyle(buttonType)}
			onClick={onClick}
		>
			{buttonSurface === 'edit-ticket-line-modal'
				? <>
					{image ? icon : null}
					{text}
				</>
				: showHelp ? text : image ? icon : text
			}
		</button>
	);
});

export const LargeDeviceActionButton = React.memo(function LargeDeviceActionButton({ title, image, fnKey, type, surface, action, visibility }: ActionButtonProps) {

	const onClick = useActionClick(action);

	const buttonType = type ?? 'primary';
	const buttonSurface = surface ?? 'footer';

	return (
		<button
			type='button'
			className={getClassName('large', buttonType, buttonSurface, visibility)}
			style={getCategoryStyle(buttonType)}
			onClick={onClick}
		>
			{fnKey !== undefined ? <span className='btn-pos-action-fn'>{fnKey}</span> : null}
			{image ? <ActionButtonIcon image={image} /> : null}
			<span className='btn-pos-action-text'>{title}</span>
		</button>
	);
});

export function ActionButton(props: ActionButtonProps) {
	const screenSize = useScreenSize();
	return ThemeManager.isPhone() || screenSize === 'small' ? <PhoneDeviceActionButton {...props} /> : <LargeDeviceActionButton {...props} />;
}
